import React, { useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import ChartTooltip from '../../components/ChartTooltip'
import { getSaberChartTheme } from '../../theme'
import type { SaberChartTheme } from '../../theme'
import type { ShadowBillingPoint } from '../../saber.types'
import type { InvoiceMarker } from './markers'

// Daily Production (kWh) chart ported from ShadowBilling (allocated vs billed
// AreaChart), extended with §7 invoice boundary markers adapted to recharts:
// one ReferenceLine per marker (dashed, green start / amber end) with a custom
// label renderer drawing the directional triangle flag, a wide invisible hit
// line carrying a native <title> tooltip, and a floating label box on hover.
// Hover emphasis is handled by tracking the hovered marker in state and
// re-rendering its ReferenceLine with a thicker stroke (recharts has no hover
// API on reference lines). The label box clamps to the LEFT chart edge only —
// the rendered chart width is unknown inside a ResponsiveContainer.

interface MarkerFlagProps {
  /** Injected by recharts when the element is used as a ReferenceLine label. */
  viewBox?: { x?: number; y?: number; width?: number; height?: number }
  marker: InvoiceMarker
  color: string
  theme: SaberChartTheme
  hovered: boolean
  onHover: (hovered: boolean) => void
  /** WEB-6572: scope the screen to this invoice's window on click. */
  onClick?: () => void
}

const MarkerFlag: React.FC<MarkerFlagProps> = ({ viewBox, marker, color, theme, hovered, onHover, onClick }) => {
  const x = viewBox?.x ?? 0
  const y = viewBox?.y ?? 0
  const height = viewBox?.height ?? 0
  const boxX = Math.max(2, x - 80)
  return (
    <g
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onClick={onClick}
      style={{ cursor: onClick != null ? 'pointer' : 'default' }}
    >
      {/* Directional triangle flag at the top: start ▸ (right), end ◂ (left). */}
      <path
        d={marker.kind === 'start' ? `M${x},${y} l6,4 l-6,4 Z` : `M${x},${y} l-6,4 l6,4 Z`}
        fill={color}
      />
      {/* Wide invisible hit line with a native tooltip. */}
      <line x1={x} x2={x} y1={y} y2={y + height} stroke="transparent" strokeWidth={12}>
        <title>{marker.label}</title>
      </line>
      {hovered && (
        <g pointerEvents="none">
          <rect x={boxX} y={y + 2} width={160} height={18} rx={4} fill={theme.tooltipBg} stroke={color} strokeWidth={1} />
          <text x={boxX + 80} y={y + 14} textAnchor="middle" fontSize={10} fill={theme.tooltipText}>
            {marker.label}
          </text>
        </g>
      )}
    </g>
  )
}

export interface ProductionChartProps {
  series: ShadowBillingPoint[]
  markers?: InvoiceMarker[]
  height?: number
  /** Shown above the chart, e.g. "Daily Production". */
  title?: string
  /** WEB-6572: clicking an invoice boundary line scopes to that invoice's window. */
  onSelectInvoiceRange?: (from: string, to: string) => void
}

export const ProductionChart: React.FC<ProductionChartProps> = ({
  series,
  markers = [],
  height = 240,
  title,
  onSelectInvoiceRange
}) => {
  const theme = getSaberChartTheme()
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)
  // §7: resolve each marker against the charted periods and drop misses.
  const resolved = markers.filter((m) => series.some((d) => d.period === m.period))

  return (
    <div className="overflow-hidden">
      {title != null && (
        <div className="mb-2 text-center text-xs font-medium text-slate-700 dark:text-slate-300">{title}</div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
          <XAxis dataKey="period" tick={{ fontSize: 10, fill: theme.axis }} stroke={theme.grid} />
          <YAxis tick={{ fontSize: 10, fill: theme.axis }} stroke={theme.grid} width={48} />
          <Tooltip content={<ChartTooltip />} />
          <Area
            type="monotone"
            dataKey="allocatedKwh"
            name="Allocated"
            stroke={theme.series[0]}
            fill={`${theme.series[0]}33`}
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="billedKwh"
            name="Billed"
            stroke={theme.series[2]}
            fill={`${theme.series[2]}22`}
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
          {resolved.map((m) => {
            const key = `${m.period}::${m.kind}`
            const color = m.kind === 'start' ? theme.ok : theme.warn
            const hovered = hoveredKey === key
            return (
              <ReferenceLine
                key={key}
                x={m.period}
                stroke={color}
                strokeDasharray="5 3"
                strokeWidth={hovered ? 2 : 1.25}
                label={
                  <MarkerFlag
                    marker={m}
                    color={color}
                    theme={theme}
                    hovered={hovered}
                    onHover={(h) => setHoveredKey(h ? key : null)}
                    onClick={onSelectInvoiceRange != null ? () => onSelectInvoiceRange(m.from, m.to) : undefined}
                  />
                }
              />
            )
          })}
        </AreaChart>
      </ResponsiveContainer>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-4 rounded-sm" style={{ background: theme.series[0] }} />
          Allocated (shadow)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4" style={{ borderTop: `2px dashed ${theme.series[2]}` }} />
          Billed
        </span>
        {resolved.length > 0 && (
          <>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 border-l-2 border-dashed" style={{ borderColor: theme.ok }} />
              Invoice start
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 border-l-2 border-dashed" style={{ borderColor: theme.warn }} />
              Invoice end
            </span>
          </>
        )}
      </div>
    </div>
  )
}

export default ProductionChart
